import type { BagelOptions } from '@bagelink/vue'
import type { PluginOptions } from 'vue-toastification'
import {
	BagelVue,
	ModalPlugin,
	RouterWrapper
} from '@bagelink/vue'
import type { Raw } from 'vue'
import type { Router } from 'vue-router'
import * as SentryVue from '@sentry/vue'
import { createPinia } from 'pinia'
import { createApp, markRaw } from 'vue'
import Toast, {
	POSITION,
	useToast
} from 'vue-toastification'

declare module 'pinia' {
	export interface PiniaCustomProperties {
		router: Raw<Router>
	}
}

import App from './App.vue'
import router from './router'
import 'vue-toastification/dist/index.css'
import '@bagelink/vue/dist/style.css'

import '@/styles/theme.css'
import '@/styles/main.css'

// // Site-wide GTM (disabled — using GTM-T6CHKTNK on /netanyahu1000 only)
// declare global {
// 	interface Window {
// 		dataLayer: any[]
// 	}
// }
//
// function initializeGTM(gtmId: string) {
// 	window.dataLayer = window.dataLayer || []
// 	const gtmScript = document.createElement('script')
// 	gtmScript.async = true
// 	gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
// 	window.dataLayer.push({
// 		'gtm.start': new Date().getTime(),
// 		event: 'gtm.js'
// 	})
// 	document.head.appendChild(gtmScript)
// 	const noscriptFrame = document.createElement('noscript')
// 	noscriptFrame.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
// 	document.body.insertBefore(noscriptFrame, document.body.firstChild)
// }

const app = createApp(App)

// Initialize Sentry if DSN is provided
if (import.meta.env.VITE_SENTRY_DSN) {
	SentryVue.init({
		app,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [
			SentryVue.browserTracingIntegration({ router }),
			SentryVue.replayIntegration(),
		],

		// Set tracesSampleRate to 1.0 to capture 100%
		// of transactions for tracing.
		// We recommend adjusting this value in production
		// Learn more at
		// https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
		tracesSampleRate: 1.0,

		// Capture Replay for 10% of all sessions,
		// plus for 100% of sessions with an error
		// Learn more at
		// https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,

		// Set the environment
		environment: import.meta.env.MODE || 'development',
	})
}

app.use(router)
const pinia = createPinia()
pinia.use(({ store }) => {
	store.router = markRaw(router)
	return {}
})
app.use(pinia as any)

const toastOptions: PluginOptions = {
	position: POSITION.TOP_RIGHT,
	icon: {
		iconClass: 'icon-font',
		iconChildren: 'info',
		iconTag: 'span',
	},
}
app.use(Toast as any, toastOptions)

// // Initialize GTM if GTM ID is provided
// const gtmId = import.meta.env.VITE_GTM_ID
// if (gtmId) {
// 	initializeGTM(gtmId)
// 	console.log(`Google Tag Manager initialized with ID: ${gtmId}`)
// } else {
// 	console.warn('GTM ID not found. Set VITE_GTM_ID environment variable to enable Google Analytics.')
// }

const bagelConfig: BagelOptions = {
	host: import.meta.env.VITE_BAGEL_BASE_URL,
	fileBaseUrl: 'https://files.bagel.design',
	onError: (err: Error & Record<string, any>) => {		
		useToast().error(`${'An Error Occurred'}: ${err.response?.data?.detail ?? err.message}`)
	},
}

app.component('RouterWrapper', RouterWrapper)
app.use(ModalPlugin as any)
app.use(BagelVue as any, bagelConfig)
app.mount('#app')
